import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';
import DataTables from 'components-desktop/DataTables';
import TfootSummary from 'components-desktop/TfootSummary';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  invoices: PropTypes.array,
};

const defaultProps = {
  title: '',
  invoices: [],
};

@autobind
class TableReceivableInvoices extends BaseComponent {
  constructor(props) {
    super(props);
  }

  createColumns() {
    return [{
      title: 'Invoice Date',
      dataIndex: 'invoiceDateUsString',
      sorter: (a, b) => a.invoiceDate - b.invoiceDate,
      defaultSortOrder: 'ascend',
    }, {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      sorter: (a, b) => a.invoiceNumber?.localeCompare(b.invoiceNumber),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/invoices/invoice_details?id=${record.id}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Auth. Code',
      dataIndex: 'authorizationCode',
      sorter: (a, b) => a.authorizationCode?.localeCompare(b.authorizationCode),
    }, {
      title: 'Company',
      dataIndex: 'companyName',
      sorter: (a, b) => a.companyName?.localeCompare(b.companyName),
    }, {
      title: 'Code',
      dataIndex: 'displayCode',
      sorter: (a, b) => a.displayCode?.localeCompare(b.displayCode),
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Alt Patient Id',
      dataIndex: 'altPatientId',
      sorter: (a, b) => a.altPatientId?.localeCompare(b.altPatientId),
    }, {
      title: 'Visits From',
      dataIndex: 'serviceStartDateUsString',
      sorter: (a, b) => a.serviceStartDate - b.serviceStartDate,
    }, {
      title: 'Visits To',
      dataIndex: 'serviceEndDateUsString',
      sorter: (a, b) => a.serviceEndDate - b.serviceEndDate,
    }, {
      title: 'Total Hours',
      dataIndex: 'totalHoursString',
      sorter: (a, b) => a.totalHours - b.totalHours,
    }, {
      title: 'Amount Billed',
      dataIndex: 'amountInDollars',
      sorter: (a, b) => a.amount - b.amount,
    }, {
      title: 'Amount Received',
      dataIndex: 'totalReceivedInDollars',
      sorter: (a, b) => a.totalReceived - b.totalReceived,
    }, {
      title: 'Balance Due',
      dataIndex: 'balanceDueInDollars',
      sorter: (a, b) => a.balanceDue - b.balanceDue,
    }, {
      title: 'Check #',
      dataIndex: 'checkNumber',
      sorter: (a, b) => a.checkNumber?.localeCompare(b.checkNumber),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/payments/payment_details?id=${record.lastPaymentId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Check Date',
      dataIndex: 'checkDateUsString',
      sorter: (a, b) => a.checkDate - b.checkDate,
    }, {
      title: 'Remark',
      dataIndex: 'remark',
      sorter: (a, b) => a.remark?.localeCompare(b.remark),
      render: (text, record) => (
        <Tooltip title={text}>
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            maxWidth="150px"
            color="textPrimaryDark"
            fontSize="12px"
            ellipsis
            display="inline-block"
          >
            {text}
          </styledComp.Span>
        </Tooltip>
      ),
    }];
  }

  createDataSource(invoices) {
    const dataSource = [];
    invoices.forEach((invoice, index) => {
      dataSource.push({
        key: invoice.getId(),
        id: invoice.getId(),
        invoiceDate: invoice.getLong('invoiceDate'),
        invoiceDateUsString: invoice.getString('invoiceDateUsString'),
        invoiceNumber: invoice.getString('invoiceNumber'),
        authorizationCode: invoice.getString('authorizationCode'),
        companyName: invoice.getString('companyName'),
        displayCode: invoice.getString('displayCode'),
        patientId: invoice.getLong('patientId'),
        patientName: invoice.getString('patientName'),
        altPatientId: invoice.getString('altPatientId'),
        serviceStartDate: invoice.getLong('serviceStartDate'),
        serviceStartDateUsString: invoice.getString('serviceStartDateUsString'),
        serviceEndDate: invoice.getLong('serviceEndDate'),
        serviceEndDateUsString: invoice.getString('serviceEndDateUsString'),
        totalHours: invoice.getLong('totalHours'),
        totalHoursString: invoice.getString('totalHoursString'),
        amount: invoice.getDouble('amount'),
        amountInDollars: helpers.toDollars(invoice.getDouble('amount')),
        totalReceived: invoice.getDouble('totalReceived'),
        totalReceivedInDollars: helpers.toDollars(invoice.getDouble('totalReceived')),
        balanceDue: invoice.getDouble('balanceDue'),
        balanceDueInDollars: helpers.toDollars(invoice.getDouble('balanceDue')),
        lastPaymentId: invoice.getRemoteObject('lastPayment')?.getId(),
        checkNumber: invoice.getRemoteObject('lastPayment')?.getString('checkNumber'),
        checkDate: invoice.getRemoteObject('lastPayment')?.getLong('checkDate'),
        checkDateUsString: invoice.getRemoteObject('lastPayment')?.getString('checkDateUsString'),
        remark: invoice.getString('remark'),
      });
    });
    return dataSource;
  }

  renderTfoot(columnsToDisplay, dataRowsToDisplay) {
    return (
      <TfootSummary
        columnsToDisplay={columnsToDisplay}
        dataRowsToDisplay={dataRowsToDisplay}
        isTableSelectable={false}
        columnKeysToAlign={['totalHoursString', 'amountInDollars', 'totalReceivedInDollars', 'balanceDueInDollars']}
        reducers={[
          TfootSummary.SUM_REDUCER_GENERATOR('totalHours'),
          TfootSummary.SUM_REDUCER_GENERATOR('amount'),
          TfootSummary.SUM_REDUCER_GENERATOR('totalReceived'),
          TfootSummary.SUM_REDUCER_GENERATOR('balanceDue')]
        }
        formatters={[
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => helpers.toDollars(n),
          n => helpers.toDollars(n),
          n => helpers.toDollars(n),
        ]}
      />
    );
  }

  renderContent() {
    const { invoices, title, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="blue_hoki"
        fontSize="12px"
        size="small"
        title={title}
        icon="file-pdf"
        columns={this.createColumns()}
        dataSource={this.createDataSource(invoices)}
        tfoot={this.renderTfoot}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableReceivableInvoices.propTypes = propTypes;
TableReceivableInvoices.defaultProps = defaultProps;

export default TableReceivableInvoices;
